import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useAutosave } from "@/hooks/useAutosave";
import api, { apiErr, isPaywall } from "@/lib/api";
import { MicButton } from "@/components/MicButton";
import { Container, PageHeader, Spinner, PaywallNotice, GenerateButton } from "@/components/shared";
import RemainingCounter from "@/components/RemainingCounter";
import PremiumHint from "@/components/PremiumHint";
import { toast } from "sonner";
import { Search, Lightbulb, MessageCircle, FileText, LifeBuoy } from "lucide-react";

const SECTIONS = [
  { key: "interpretations", tk: "sec_interpretations", icon: Lightbulb },
  { key: "calm_responses", tk: "sec_calm", icon: MessageCircle },
  { key: "documentation_tips", tk: "sec_doc", icon: FileText },
  { key: "resources", tk: "sec_resources", icon: LifeBuoy },
];

export default function Analyze() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const isChild = user?.profile === "child";
  const [params] = useSearchParams();
  const [situation, setSituation] = useState("");
  useAutosave("analyze", situation, setSituation);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [paywall, setPaywall] = useState(false);
  const [usageTrigger, setUsageTrigger] = useState(0);

  const analyze = async (text) => {
    const s = (typeof text === "string" ? text : situation).trim();
    if (!s) return;
    setBusy(true); setPaywall(false); setResult(null);
    try {
      const { data } = await api.post("/ai/analyze", { situation: s });
      setResult(data);
      setUsageTrigger((t) => t + 1);
    } catch (e) {
      if (isPaywall(e)) setPaywall(true);
      else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  useEffect(() => {
    const s = params.get("situation");
    if (s) setSituation(s);
    if (s && params.get("auto") === "1") analyze(s);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  return (
    <Container>
      <PageHeader eyebrow={t("pages.analyze.eyebrow")} title={t("pages.analyze.title")}
        subtitle={t("pages.analyze.subtitle")} />

      <div className="mb-4"><RemainingCounter kind="analyze" trigger={usageTrigger} /></div>


      <div className="card-surface p-5 mb-6">
        <textarea data-testid="analyze-input" rows={5} value={situation}
          onChange={(e) => setSituation(e.target.value)}
          placeholder={isChild ? t("pages.analyze.placeholderChild") : t("pages.analyze.placeholder")}
          className="w-full bg-transparent outline-none resize-none text-white placeholder:text-white/30" />
        <div className="flex items-center justify-between gap-3 mt-3">
          <MicButton testid="analyze-input-mic" value={situation} onChange={setSituation} />
          <GenerateButton data-testid="analyze-button" onClick={analyze} disabled={busy || !situation.trim()}>
            <Search size={16} /> {t("pages.analyze.analyze")}
          </GenerateButton>
        </div>
      </div>

      {busy && <Spinner label={t("pages.analyze.spinner")} />}
      {paywall && <PaywallNotice />}

      {result && (
        <div className="space-y-4">
          {SECTIONS.map((s) => {
            const items = result[s.key] || [];
            const Icon = s.icon;
            return (
              <div key={s.key} data-testid={`analyze-section-${s.key}`} className="card-surface p-5 fade-up">
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={18} className="text-gold" strokeWidth={1.5} />
                  <h3 className="font-display text-lg">{t(`pages.analyze.${s.tk}`)}</h3>
                </div>
                <ul className="space-y-2">
                  {items.map((it, i) => (
                    <li key={i} className="text-white/80 text-sm leading-relaxed flex gap-2">
                      <span className="text-gold/60">—</span> {it}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      <PremiumHint text={t("pages.analyze.premiumHint")} />
    </Container>
  );
}
